import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/roleHome";
import {
  Users, ClipboardList, BedDouble, Ambulance, IndianRupee, Wallet, Activity, Scissors, Calendar as CalIcon,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";

const KPI = ({ label, value, sub, icon: Icon, tone = "blue", testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-tiny">{label}</div>
        <div className="mt-2 font-display text-2xl font-semibold text-slate-900 data-mono">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`w-9 h-9 flex items-center justify-center bg-${tone}-50 text-${tone}-700`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
    </div>
  </div>
);

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const PRESETS = [
  { label: "Today", from: today(), to: today() },
  { label: "Yesterday", from: daysAgo(1), to: daysAgo(1) },
  { label: "This Week", from: daysAgo(6), to: today() },
  { label: "This Month", from: new Date().toISOString().slice(0, 7) + "-01", to: today() },
  { label: "Last 30 Days", from: daysAgo(29), to: today() },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard", { params: { from_date: from, to_date: to } }).then((r) => setD(r.data));
  }, [from, to]);

  // Non-admin roles → redirect to their dedicated home so they never see the finance dashboard
  if (user && !["super_admin", "admin"].includes(user.role)) {
    const home = homeFor(user.role);
    if (home !== "/") return <Navigate to={home} replace />;
  }

  if (!d) return <div className="p-8 text-slate-500">Loading dashboard...</div>;
  const COLORS = ["#1D4ED8", "#0D9488", "#D97706", "#7C3AED", "#DC2626", "#0369A1"];
  const rangeLabel = from === to ? new Date(from).toLocaleDateString("en-IN") : `${new Date(from).toLocaleDateString("en-IN")} → ${new Date(to).toLocaleDateString("en-IN")}`;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Hospital Dashboard</h1>
          <p className="text-sm text-slate-500">KPIs for {rangeLabel}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="qa-new-patient" onClick={() => nav("/patients?new=1")} className="bg-blue-700 hover:bg-blue-800 rounded-sm">+ New Patient</Button>
          <Button data-testid="qa-new-opd" variant="outline" onClick={() => nav("/opd?new=1")} className="rounded-sm">+ OPD</Button>
          <Button data-testid="qa-new-ipd" variant="outline" onClick={() => nav("/ipd?new=1")} className="rounded-sm">+ IPD</Button>
          <Button data-testid="qa-emergency" variant="outline" onClick={() => nav("/emergency?new=1")} className="rounded-sm">+ Emergency</Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white border border-slate-200 p-3 flex items-center gap-3 flex-wrap">
        <CalIcon className="w-4 h-4 text-blue-700" />
        <div className="flex items-center gap-2">
          <label className="label-tiny">From</label>
          <Input type="date" data-testid="dash-from" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" />
          <label className="label-tiny ml-2">To</label>
          <Input type="date" data-testid="dash-to" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" />
        </div>
        <div className="flex gap-1 flex-wrap ml-2">
          {PRESETS.map((p) => (
            <button key={p.label} data-testid={`preset-${p.label.replace(/\s/g,'-').toLowerCase()}`} onClick={() => { setFrom(p.from); setTo(p.to); }} className={`text-xs px-2.5 py-1 border rounded-sm ${from === p.from && to === p.to ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:border-blue-500"}`}>{p.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI testid="kpi-opd" label="OPD" value={d.today.opd} icon={ClipboardList} tone="blue" />
        <KPI testid="kpi-ipd" label="IPD" value={d.today.ipd} icon={BedDouble} tone="teal" />
        <KPI testid="kpi-emergency" label="Emergency" value={d.today.emergency} icon={Ambulance} tone="red" />
        <KPI testid="kpi-discharge" label="Discharges" value={d.today.discharge} icon={Activity} tone="amber" />
        <KPI testid="kpi-collection" label="Total Collection" value={fmtINR(d.today.collection)} icon={IndianRupee} tone="blue" />
        <KPI testid="kpi-advance" label="Advance" value={fmtINR(d.today.advance)} icon={Wallet} tone="teal" />
        <KPI testid="kpi-refund" label="Refund" value={fmtINR(d.today.refund)} icon={Wallet} tone="red" />
        <KPI testid="kpi-beds" label="Bed Occupancy" value={`${d.beds.occupied}/${d.beds.total}`} sub={`ICU ${d.beds.icu_occupied}/${d.beds.icu}`} icon={BedDouble} tone="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-medium">Weekly Trend</h3>
            <div className="label-tiny">OPD · IPD · Revenue</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" fontSize={11} stroke="#64748B" />
              <YAxis fontSize={11} stroke="#64748B" />
              <Tooltip />
              <Legend fontSize={11} />
              <Line type="monotone" dataKey="opd" stroke="#1D4ED8" strokeWidth={2} />
              <Line type="monotone" dataKey="ipd" stroke="#0D9488" strokeWidth={2} />
              <Line type="monotone" dataKey="revenue" stroke="#D97706" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="font-display font-medium mb-3">Payment Modes</h3>
          {d.modes.length === 0 ? (
            <div className="text-sm text-slate-500 h-[260px] flex items-center justify-center">No payments in this range</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={d.modes} dataKey="value" nameKey="name" outerRadius={80} label>
                  {d.modes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend fontSize={11} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="font-display font-medium mb-3">Department-wise OPD</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.departments}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" fontSize={10} stroke="#64748B" angle={-20} textAnchor="end" height={60} />
              <YAxis fontSize={11} stroke="#64748B" />
              <Tooltip />
              <Bar dataKey="count" fill="#1D4ED8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="font-display font-medium mb-3">Quick Actions</h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              { to: "/patients?new=1", label: "New Patient", icon: Users },
              { to: "/opd?new=1", label: "New OPD", icon: ClipboardList },
              { to: "/ipd?new=1", label: "New IPD", icon: BedDouble },
              { to: "/emergency?new=1", label: "Emergency", icon: Ambulance },
              { to: "/billing", label: "Billing", icon: IndianRupee },
              { to: "/ot", label: "OT / Surgery", icon: Scissors },
              { to: "/investigations", label: "Investigation", icon: Activity },
              { to: "/pharmacy", label: "Pharmacy", icon: Wallet },
              { to: "/reports", label: "Reports", icon: ClipboardList },
            ].map((a) => (
              <Link key={a.to} to={a.to} className="flex flex-col items-center gap-2 border border-slate-200 hover:border-blue-500 hover:bg-blue-50 p-3 rounded-sm">
                <a.icon className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
                <span className="text-[11px] text-slate-700">{a.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
